import { useEffect, useRef, useState } from "react"
import { FaCircleCheck } from "react-icons/fa6"
import { useNavigate } from "react-router-dom"
import { Box, Center, HStack, Link, Separator, Spacer, Stack, VStack } from "@chakra-ui/react"
import { Button, Input, Text, TextField } from "@/components"
import { useAuth, useViewer } from "@/contexts"
import { useInsertNda, useVerifyPhone, useResendPhoneOtp, useSetTwoFactor } from "@/hooks"
import { OnboardingComponent } from "./OnboardingComponent"
import { CodeTarget, codeSentSentence, codeTargetFrom } from "@/utils"

interface Props {
  isInitialScreen: boolean
}

type PostSubmitStep = "verify-phone" | "two-factor-optin" | "complete"

export const NdaFormComponent = ({ isInitialScreen }: Props) => {
  const formRef = useRef<HTMLDivElement>(null)
  const navigate = useNavigate()
  const { user } = useAuth()
  const { viewedClient } = useViewer()
  const { mutateAsync: insertNdaInfo, isPending } = useInsertNda()
  const { mutateAsync: verifyPhone, isPending: isVerifyingPhone } = useVerifyPhone()
  const { mutateAsync: resendOtp, isPending: isResending } = useResendPhoneOtp()
  const { mutateAsync: setTwoFactor, isPending: isSettingTwoFactor } = useSetTwoFactor()

  const [firstName, setFirstName] = useState<string>("")
  const [middleName, setMiddleName] = useState<string>("")
  const [lastName, setLastName] = useState<string>("")
  const [suffix, setSuffix] = useState<string>("")
  const [preferredEmail, setPreferredEmail] = useState<string>("")
  const [address, setAddress] = useState<string>("")

  useEffect(() => {
    if (viewedClient) {
      setFirstName(viewedClient.firstName)
      setLastName(viewedClient.lastName)
      setPreferredEmail(viewedClient.email)
    } else if (user && user.userType === "client") {
      setFirstName(user.firstName)
      setLastName(user.lastName)
      setPreferredEmail(user.email)
    }
  }, [user, viewedClient])
  const [city, setCity] = useState<string>("")
  const [state, setState] = useState<string>("")
  const [zipCode, setZipCode] = useState<string>("")
  const [country, setCountry] = useState<string>("")
  const [phoneCountry, setPhoneCountry] = useState<string>("")
  const [phoneNumber, setPhoneNumber] = useState<string>("")

  // Post-submission state
  const [postSubmitStep, setPostSubmitStep] = useState<PostSubmitStep | null>(null)
  const [phoneLast4, setPhoneLast4] = useState<string>("")
  // Where the code went: a text (our number, or Twilio Verify abroad) or the sign-in email.
  const [codeTarget, setCodeTarget] = useState<CodeTarget>({ channel: "sms", destination: "" })
  const [otpCode, setOtpCode] = useState<string>("")
  const [otpError, setOtpError] = useState<string>("")
  const [resendSuccess, setResendSuccess] = useState<boolean>(false)

  if (!user) return null

  const scrollToForm = () => {
    formRef.current?.scrollIntoView({ behavior: "smooth" })
  }

  const handleSubmitClick = async ({
    firstName,
    middleName,
    lastName,
    preferredEmail,
    suffix,
    address,
    city,
    state,
    zipCode,
    country,
    phoneNumber
  }: {
    firstName: string,
    middleName: string,
    lastName: string,
    preferredEmail: string,
    suffix: string,
    address: string,
    city: string,
    state: string,
    zipCode: string,
    country: string,
    phoneNumber: string
  }) => {
    if (user.userType !== "client") return
    try {
      if (firstName === "" ||
        lastName === "" ||
        preferredEmail === "" ||
        address === "" ||
        city === "" ||
        state === "" ||
        zipCode === "" ||
        phoneNumber === ""
      ) {
        alert("Please fill out all required fields.")
        return
      }

      const result = await insertNdaInfo({
        legalName: [firstName, middleName, lastName, suffix].filter(Boolean).join(" "),
        email: preferredEmail,
        address,
        city,
        state,
        zip: zipCode,
        country: country || phoneCountry || undefined,
        phone: phoneNumber,
        dealManagerName: `${user.dealManager.firstName} ${user.dealManager.lastName}`,
        dealManagerEmail: user.dealManager.email
      })

      // The form went through but no code could be sent at all (or an API from before
      // 2026-10-16 could not text a number outside the US and Canada): skip the check.
      if (result.codeSent === false || (result.smsUnreachable && !result.channel)) {
        setPostSubmitStep("complete")
        return
      }
      setPhoneLast4(result.phoneLast4)
      setCodeTarget(codeTargetFrom(result))
      setPostSubmitStep("verify-phone")
    } catch (error) {
      console.error("Error inserting NDA information:", error)
    }
  }

  const handleVerifyPhone = async () => {
    try {
      setOtpError("")
      const verified = await verifyPhone({ otp: otpCode })
      // An emailed code proves the inbox, not the phone, so there is no two-factor offer then.
      setPostSubmitStep(verified?.phoneVerified === false ? "complete" : "two-factor-optin")
    } catch {
      setOtpError("Invalid or expired code. Please try again.")
    }
  }

  const handleResendCode = async () => {
    try {
      setResendSuccess(false)
      const sent = await resendOtp(codeTarget.channel === "email" ? { channel: "email" } : undefined)
      setCodeTarget(codeTargetFrom(sent))
      setResendSuccess(true)
    } catch {
      setOtpError("Failed to resend code. Please try again.")
    }
  }

  const handleEmailInstead = async () => {
    try {
      setOtpError("")
      setOtpCode("")
      setResendSuccess(false)
      const sent = await resendOtp({ channel: "email" })
      setCodeTarget(codeTargetFrom(sent))
      setResendSuccess(true)
    } catch {
      setOtpError("We couldn't email a code. Please try again.")
    }
  }

  const handleTwoFactorChoice = async (enabled: boolean) => {
    try {
      await setTwoFactor(enabled)
    } catch (error) {
      console.error("Error setting 2FA preference:", error)
    }
    setPostSubmitStep("complete")
  }

  // Step 1: Phone verification
  if (postSubmitStep === "verify-phone") {
    return (
      <Center w="100%" h="100%">
        <VStack position="relative" zIndex={1} w={{ base: "92%", md: "40%" }} p={8} borderRadius={10} bg="white" boxShadow="md" mt={20} gap={4} align="center">
          <Box borderRadius={5} bg="blue.300" p={3}>
            <FaCircleCheck size={30} color="white" />
          </Box>
          <Text fontSize="lg" fontWeight="bold" textAlign="center">
            {codeTarget.channel === "email" ? "Check Your Email for a Code" : "Verify Your Phone Number"}
          </Text>
          <Text fontSize="sm" textAlign="center">
            {codeSentSentence(codeTarget, phoneLast4)} Please enter it below.
          </Text>
          <Input
            placeholder="Enter 6-digit code"
            type="text"
            value={otpCode}
            maxLength={6}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              setOtpCode(e.target.value)
              setOtpError("")
              setResendSuccess(false)
            }}
          />
          {otpError && (
            <Text fontSize="sm" color="red.500">{otpError}</Text>
          )}
          {resendSuccess && (
            <Text fontSize="sm" color="green.500">A new code has been sent.</Text>
          )}
          <Button
            variant="solid"
            w="100%"
            loading={isVerifyingPhone}
            disabled={isVerifyingPhone || otpCode.length !== 6}
            onClick={handleVerifyPhone}
          >
            Verify
          </Button>
          <Text
            fontSize="sm"
            color="blue.500"
            cursor="pointer"
            _hover={{ textDecoration: "underline" }}
            onClick={!isResending ? handleResendCode : undefined}
          >
            {isResending ? "Sending..." : "Resend Code"}
          </Text>
          {/* Only a Twilio Verify text (a number outside the US and Canada) offers email. */}
          {codeTarget.channel === "verify" && (
            <Text
              fontSize="sm"
              color="gray.600"
              cursor="pointer"
              _hover={{ textDecoration: "underline" }}
              onClick={!isResending ? handleEmailInstead : undefined}
            >
              Didn't get a text? Email me a code instead
            </Text>
          )}
        </VStack>
      </Center>
    )
  }

  // Step 2: 2FA opt-in
  if (postSubmitStep === "two-factor-optin") {
    return (
      <Center w="100%" h="100%">
        <VStack position="relative" zIndex={1} w={{ base: "92%", md: "40%" }} p={8} borderRadius={10} bg="white" boxShadow="md" mt={20} gap={4}>
          <Box borderRadius={5} bg="green.300" p={3}>
            <FaCircleCheck size={30} color="white" />
          </Box>
          <Text fontSize="lg" fontWeight="bold" textAlign="center">
            Phone Verified!
          </Text>
          <Text fontSize="sm" textAlign="center">
            Would you like to enable two-factor authentication for added security? When enabled, you will receive a verification code via SMS each time you log in.
          </Text>
          <Stack direction={{ base: "column", md: "row" }} gap={4} w="100%">
            <Button
              variant="outline"
              flex={1}
              loading={isSettingTwoFactor}
              disabled={isSettingTwoFactor}
              onClick={() => handleTwoFactorChoice(false)}
            >
              No Thanks
            </Button>
            <Button
              variant="solid"
              flex={1}
              loading={isSettingTwoFactor}
              disabled={isSettingTwoFactor}
              onClick={() => handleTwoFactorChoice(true)}
            >
              Enable 2FA
            </Button>
          </Stack>
        </VStack>
      </Center>
    )
  }

  // Step 3: Onboarding (on /nda) or Thank You (on /compliance)
  if (postSubmitStep === "complete") {
    if (isInitialScreen) {
      return <OnboardingComponent />
    }

    return (
      <Center w="100%" h="100%">
        <VStack position="relative" zIndex={1} w={{ base: "92%", md: "40%" }} p={5} borderRadius={10} bg="white" boxShadow="md" mt={20}>
          <Box borderRadius={5} bg="green.300" p={3}>
            <FaCircleCheck size={30} color="white" />
          </Box>
          <Text fontSize="lg" fontWeight="bold" textAlign="center" mt={2} mb={2}>
            Thank You!
          </Text>
          <Text fontSize="sm" textAlign="center" mb={5}>
            Thank you for signing our Client Information Form. Please check your email for a DocuSign link sent from docusign@esofund.com to finish signing the Mutual NDA.
          </Text>
          <Text fontSize="sm" textAlign="center">If you didn't receive an email please contact your Equity Partner.</Text>
          <Button variant="solid" onClick={() => navigate(viewedClient ? `/clients/${viewedClient.id}` : "/documents")}>Proceed to the Portal</Button>
        </VStack>
      </Center>
    )
  }

  return (
    <Stack as="form" onSubmit={(e: React.FormEvent) => { e.preventDefault(); handleSubmitClick({ firstName, middleName, lastName, preferredEmail, suffix, address, city, state, zipCode, country, phoneNumber }) }} w={{ base: "100%", md: isInitialScreen ? "60%" : "100%" }} h="100vh" bg="white" p={{ base: 5, md: 10 }} borderLeftRadius={{ base: 0, md: 20 }} overflowY="auto">
      <Text fontSize="2xl" fontWeight="bold" fontFamily="body" mb={5}>
        Welcome! We're excited to work with you!
      </Text>
      <Text fontSize="md" mb={5}>
        To get started, please fill out the{" "}
      <Text
        as="span"
        color="blue.500"
        fontWeight="bold"
        cursor="pointer"
        _hover={{ textDecoration: "underline" }}
        onClick={scrollToForm}
      >
        form below
      </Text>{" "}with your details. Once completed you will receive a DocuSign link via email to sign the Mutual Non-Disclosure Agreement (MNDA).
      </Text>
      <Text>
        This MNDA protects you and any information shared between you and ESO Fund. It also prohibits the sharing of ESO Fund materials with anyone other than the professional advisors assisting you on this transaction.
      </Text>

      <Text mt={5}>
        Once signed, you'll be asked to submit information about your equity holdings. As soon as we receive it, we'll work to get you an offer as quickly as possible.
      </Text>

      <Text>
        For an overview of our funding process, please go to our{" "}
        <Text as={Link} color="blue.500" fontWeight="bold" onClick={() => window.open("https://esofund.com/overview", "_blank", "noopener,noreferrer")}>
          website
        </Text>.
      </Text>
      <Separator mt={5} mb={5} />
      <Stack gap={10} ref={formRef}>
        <Stack direction={{ base: "column", md: "row" }} gap={4} mt={4}>
          <TextField
            label="First Name"
            placeholder="Enter your first name"
            type="text"
            name="given-name"
            autoComplete="given-name"
            value={firstName}
            required
            onChange={(e) => setFirstName(e)}
          />
          <TextField
            label="Middle Name"
            placeholder="Enter your middle name"
            name="additional-name"
            autoComplete="additional-name"
            value={middleName}
            onChange={(e) => setMiddleName(e)}
          />
        </Stack>
        <Stack direction={{ base: "column", md: "row" }} gap={4}>
          <TextField
            label="Last Name"
            placeholder="Enter your last name"
            type="text"
            name="family-name"
            autoComplete="family-name"
            value={lastName}
            required
            onChange={(e) => setLastName(e)}
          />
          <TextField
            label="Suffix"
            placeholder="Enter your suffix"
            name="honorific-suffix"
            autoComplete="honorific-suffix"
            value={suffix}
            onChange={(e) => setSuffix(e)}
          />
        </Stack>

        <Stack direction={{ base: "column", md: "row" }} gap={4}>
          <TextField
            label="Preferred Email"
            placeholder="Enter your preferred email"
            type="email"
            name="email"
            autoComplete="email"
            value={preferredEmail}
            required
            onChange={(e) => setPreferredEmail(e)}
          />
          <TextField
            label="Phone Number"
            placeholder="Enter your phone number"
            type="phone"
            name="tel"
            autoComplete="tel"
            value={phoneNumber}
            required
            onChange={(e) => setPhoneNumber(e)}
            onPhoneCountryChange={setPhoneCountry}
          />
        </Stack>

        <TextField
          label="Address"
          placeholder="Enter your address"
          type="text"
          name="street-address"
          autoComplete="street-address"
          value={address}
          required
          onChange={(e) => setAddress(e)}
        />

        <TextField
          label="City"
          placeholder="Enter your city"
          type="text"
          name="address-level2"
          autoComplete="address-level2"
          value={city}
          required
          onChange={(e) => setCity(e)}
        />

        <Stack direction={{ base: "column", md: "row" }} gap={4}>
          <TextField
            label="State"
            placeholder="Enter your state"
            type="text"
            name="address-level1"
            autoComplete="address-level1"
            value={state}
            required
            onChange={(e) => setState(e)}
          />
          <TextField
            label="Zip Code"
            placeholder="Enter your zip code"
            type="text"
            name="postal-code"
            autoComplete="postal-code"
            value={zipCode}
            required
            onChange={(e) => setZipCode(e)}
          />
        </Stack>

        <TextField
          label="Country"
          placeholder="Enter your country"
          type="text"
          name="country-name"
          autoComplete="country-name"
          value={country}
          onChange={(e) => setCountry(e)}
        />
      </Stack>
      <HStack>
        <Spacer />
        <Button
          type="submit"
          variant="solid"
          w="fit-content"
          mt={5}
          loading={isPending}
          disabled={isPending}
        >
          Submit
        </Button>
      </HStack>
    </Stack>
  )
}
